import {
  Typography,
  Card,
  StackedList,
  BannerWrapper,
  ActionModal,
  DialogPosition,
} from '@ecdlink/ui';
import React, { useCallback } from 'react';
import { useOnlineStatus } from '@hooks/useOnlineStatus';
import {
  formatCurrency,
  numberWithSpaces,
  sumIncomeOrExpenseItems,
} from '@/utils/statements/statements-utils';
import { getMonthName } from '@/utils/classroom/attendance/track-attendance-utils';
import { format } from 'date-fns';
import { ExpenseDetailsListProps } from './expense-details-list.types';
import { Dialog } from '@headlessui/react';
import { useDialog } from '@ecdlink/core';
import { useHistory } from 'react-router';
import ROUTES from '@/routes/routes';
import { useAppDispatch } from '@/store';
import {
  statementsSelectors,
  statementsThunkActions,
} from '@/store/statements';
import { useSelector } from 'react-redux';

export const ExpenseDetailsList: React.FC<ExpenseDetailsListProps> = ({
  hideDetails,
  statementTitle,
  expenseItems,
  statementMonth,
}) => {
  const { isOnline } = useOnlineStatus();
  const dialog = useDialog();
  const history = useHistory();
  const appDispatch = useAppDispatch();
  const unSubmittedExpenseItems = useSelector(
    statementsSelectors.getUnsubmittedExpenseItems
  );

  const unsyncedDeletedExpenseItemIds = useSelector(
    statementsSelectors.getUnsyncedDeletedExpenseItemIds
  );

  // Derived from the prop rather than copied into local state. The old local copy was seeded once
  // on mount, so it could not reflect the store - a removed entry came straight back when this
  // screen was remounted. Deletions still queued for sync are filtered out as well, so an entry
  // removed offline stays hidden even if the statement is refetched before the queue is replayed.
  const expenseItemsList = React.useMemo(
    () =>
      (expenseItems || []).filter(
        (item) => !unsyncedDeletedExpenseItemIds?.includes(item?.id)
      ),
    [expenseItems, unsyncedDeletedExpenseItemIds]
  );

  const hasExpenseUnsubmittedItem = unSubmittedExpenseItems?.find(
    (item) => item?.id === expenseItemsList?.[0]?.id
  );
  const isStatementNotSubmitted = !!hasExpenseUnsubmittedItem;

  const expenseListDetailsItems = expenseItemsList?.map((item) => {
    return {
      title: !!item.description
        ? item.description
        : format(Date.parse(item.datePaid || ''), 'dd/MM/yyyy'),
      titleStyle: 'text-textDark font-semibold text-base leading-snug',
      subTitleStyle:
        'text-sm font-h1 font-normal text-textMid w-9/12 overflow-clip',
      text: '1',
      onActionClick: () => {},
      classNames: 'bg-uiBg',
      subItem: `R ${numberWithSpaces(String(item?.amount!.toFixed(2)))}`,
      notRounded: true,
      childList: true,
      isEditable: isStatementNotSubmitted ? true : false,
      onRemoveActionClick: () => removeStatementDialog(item?.id),
      onEditActionClick: () => {
        history.push(ROUTES.BUSINESS_ADD_EXPENSE, {
          expenseItem: item,
          isEdit: true,
        });
      },
    };
  });

  const handleDelete = useCallback(
    async (expenseItemId: string) => {
      await appDispatch(
        statementsThunkActions.deleteExpenseItem({ id: expenseItemId })
      );

      // The store now removes the item from the statement, online or offline, so there is no
      // local list to keep in step.
    },
    [appDispatch]
  );

  const removeStatementDialog = (id: string) => {
    dialog({
      position: DialogPosition.Middle,
      render: (onSubmit, onCancel) => (
        <Dialog.Panel>
          <ActionModal
            icon={'InformationCircleIcon'}
            iconColor="alertMain"
            iconBorderColor="alertBg"
            importantText={`Are you sure you want to remove this transaction?`}
            detailText={
              'This transaction will be permanently removed. and will no longer reflect on your statement'
            }
            actionButtons={[
              {
                text: 'Yes, remove',
                textColour: 'white',
                colour: 'primary',
                type: 'filled',
                onClick: () => {
                  handleDelete(id);
                  onSubmit();
                  onCancel();
                },
                leadingIcon: 'ArrowLeftIcon',
              },
              {
                text: 'Close',
                textColour: 'primary',
                colour: 'primary',
                type: 'outlined',
                onClick: () => onCancel(),
                leadingIcon: 'PencilIcon',
              },
            ]}
          />
        </Dialog.Panel>
      ),
    });
  };

  return (
    <BannerWrapper
      showBackground={false}
      size="medium"
      renderBorder={true}
      title={`View ${getMonthName(Number(statementMonth))} preschool statement`}
      color={'primary'}
      onBack={hideDetails}
      displayOffline={!isOnline}
    >
      <div className="flex flex-col justify-center p-4">
        <Typography
          className="truncate"
          type="h2"
          weight="bold"
          color="textDark"
          text={statementTitle}
        />
        <Typography
          className="truncate"
          type="body"
          weight="bold"
          color="textMid"
          text={`${getMonthName(Number(statementMonth))} expenses`}
        />
        {expenseListDetailsItems && (
          <StackedList
            className="mt-4 flex w-full flex-col"
            type="MenuList"
            listItems={expenseListDetailsItems}
          />
        )}
        <Card
          className="bg-secondary flex items-center justify-between p-4"
          shadowSize={'md'}
        >
          <Typography
            text={'Total expense'}
            type="body"
            color={'white'}
            className="w-8/12"
          />
          <Typography
            text={`R ${formatCurrency(
              sumIncomeOrExpenseItems(expenseItemsList)
            )}`}
            color={'white'}
            type="h4"
            className="mr-12 w-4/12 text-right"
          />
        </Card>
      </div>
    </BannerWrapper>
  );
};
